import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { MarketTemplatePanel, Message, TryAgainMessage } from '@swf/sports/components';
import MarketGroupTabs from '@swf/sports/components/MarketGroupTabs/MarketGroupTabs';
import useDisplayableMarketGroups from '@swf/sports/hooks/useDisplayableMarketGroups/useDisplayableMarketGroups';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import { isCompoundMarketTemplate } from '@swf/sports/utils/isCompoundMarketTemplate';

import { TXTWCL_There_are_no_markets_available_ELL } from './SportsRacePageWidget.messages.json';
import styles from './SportsRacePageWidget.scss';
import EventFinishedMessage from './components/EventFinishedMessage/EventFinishedMessage';
import EventNotFoundMessage from './components/EventNotFoundMessage/EventNotFoundMessage';
import LoadingState from './components/LoadingState/LoadingState';
import MeetingFinishedMessage from './components/MeetingFinishedMessage/MeetingFinishedMessage';
import RaceWrapper from './components/RaceWrapper/RaceWrapper';
import TimeFormDropdown from './components/TimeFormDropdown/TimeFormDropdown';
import { sportsRacePageWidgetModel, useSportsRacePageWidget } from './model';

const SportsRacePageWidget: WidgetFC = () => {
  const { eventTypeId, eventTypeSlug, venueSlug, meetingId, raceId } = useSelector(
    select.sports.getUrlParams
  );
  const { widgetSelect, widgetDispatch } = useSportsRacePageWidget();
  const isLoading = useSelector(widgetSelect.isLoading);
  const groups = useSelector(widgetSelect.groups);
  const marketIds = useSelector(widgetSelect.marketIds);
  const markets = useSelector(widgetSelect.markets);
  const errorCode = useSelector(widgetSelect.errorCode);
  const cachedRace = useSelector(widgetSelect.cachedRace);
  const cachedRaces = useSelector(widgetSelect.cachedRaces);
  const hasTimeformData = useSelector(widgetSelect.hasTimeformData);
  const race = useSelector(widgetSelect.currentRace);
  const races = useSelector(widgetSelect.orderedRaces);
  const { hash } = useLocation();
  const shouldShowFinishedMessage = cachedRace && cachedRaces.length;

  const [marketGroupSlug, setMarketGroupSlug] = useState('');

  const [displayableMarketGroups, currentGroup] = useDisplayableMarketGroups({
    markets,
    groups,
    currentMarketGroupSlug: marketGroupSlug
  });

  const config = {
    eventTypeSlug,
    eventTypeId,
    venueSlug,
    meetingId,
    raceId
  };

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  useEffect(() => {
    setMarketGroupSlug(hash.replace('#', ''));
  }, [hash]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (errorCode === 404) {
    return shouldShowFinishedMessage ? (
      <RaceWrapper race={cachedRace} races={cachedRaces}>
        <MeetingFinishedMessage />
      </RaceWrapper>
    ) : (
      <EventNotFoundMessage />
    );
  }

  if (typeof errorCode !== 'undefined') {
    return <TryAgainMessage />;
  }

  if (!race) {
    return shouldShowFinishedMessage ? (
      <RaceWrapper race={cachedRace} races={cachedRaces}>
        <EventFinishedMessage />
      </RaceWrapper>
    ) : (
      <EventNotFoundMessage />
    );
  }

  return (
    <RaceWrapper race={race} races={races}>
      <div data-testid="sports-race-card">
        {hasTimeformData ? <TimeFormDropdown /> : null}
        <div className={styles.tabsContainer}>
          <MarketGroupTabs
            groups={displayableMarketGroups}
            routeValues={config}
            marketGroupSlug={marketGroupSlug}
            entities={{
              raceId: race.raceId,
              sportId: Number(race.eventTypeId),
              meetingId: race.meetingId,
              eventStartTime: race.startTime
            }}
          />
        </div>
        {currentGroup ? (
          currentGroup.templates.map((template, index) => {
            if (isCompoundMarketTemplate(template)) return null;

            return (
              <MarketTemplatePanel
                key={`${currentGroup.name}-${template.marketId}-${index}`}
                isOpenInitially={index < 5}
                {...template}
              />
            );
          })
        ) : (
          <Message message={<Translation {...TXTWCL_There_are_no_markets_available_ELL} />} />
        )}
      </div>
    </RaceWrapper>
  );
};

SportsRacePageWidget.model = sportsRacePageWidgetModel;

export default withWidgetTestId(SportsRacePageWidget, 'sports-race-page');
